import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import * as XLSX from "xlsx";
import { useNavigate } from 'react-router-dom';
import {
    Card,
    CardContent,
    Divider,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    TextField,
    CardHeader
} from '@mui/material';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import { useNotification } from 'config/Notification';
import {
    fetchAllStandards as fetchStandardsService,
    saveNewStandard,
    createNewStandard,
    updateStandardById,
    syncMasterStandardsById
} from 'services/modules/cap/standardservice';
import { CloudUploadOutlined } from '@ant-design/icons';
import { Upload } from 'antd';
import Breadcrumbs from 'config/Breadcrumb';

const ConfigureStandards = () => {
    const standardTypes = [
        { id: 'standard-based', name: 'Standard Based' },
        { id: 'domain-based', name: 'Domain Based' }
    ];

    const [standardType, setStandardType] = useState(standardTypes[0].id);
    const [selectedStandards, setSelectedStandards] = useState([]);

    const { openNotification } = useNotification();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isStep1Editing, setIsStep1Editing] = useState(false)

    const [open, setOpen] = React.useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [fileUploadDialog, setFileUploadDialog] = useState(false);
    const [fileData, setFileData] = useState([]);
    const [fileList, setFileList] = useState([]);
    const { Dragger } = Upload;
    // React router
    const navigate = useNavigate();

    const [openEdit, setOpenEdit] = useState(false);

    // Fetch standards data based on the selected standard type
    const fetchStandardsData = async () => {
        try {
            const response = await fetchStandardsService(
                standardType === 'standard-based' ? 'Main Standard' : 'Domain Standard'
            );
            setSelectedStandards(response.data);
        } catch (error) {
            openNotification("Error while fetching data", 'error');
        }
    };



    // This method handles to change the pages
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // This method handles to change the rows per page based on user selection
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };



    // Validation schemas for each step
    const validationSchemaStep1 = Yup.object({
        name: Yup.string().required('Required'),
        //description: Yup.string().required('Required'),
        // acronym: Yup.string().required('Required'),
        // group: Yup.string().required('Required'),
    });
    // Formik setup for step 1
    const formikStep1 = useFormik({
        initialValues: {
            name: '',
            description: '',
            acronym: '',
            group: '',
            status: true
        },
        validationSchema: validationSchemaStep1,
        onSubmit: async (values) => {
            try {
                // Check if there's an ID to determine if it's an edit

                let body = {
                    name: values.name,
                    description: values.description,
                    group: standardType === 'standard-based' ? 'Main Standard' : 'Domain Standard',
                    status: values.status
                };

                let response;

                if (isStep1Editing) {
                    let standarId = values.id;
                    response = await updateStandardById(standarId, { body }); // Update API
                    if (response.status === 200) {
                        openNotification("Standard updated successfully", 'success');
                        formikStep1.resetForm();
                        fetchStandardsData();
                        setOpenEdit(false)
                        //     setIsStep1Editing(false);
                    }
                } else {
                    response = await saveNewStandard({ body });
                    if (response.status === 201) {
                        openNotification("Standard saved successfully", 'success');
                        formikStep1.resetForm();
                        fetchStandardsData();
                        setIsStep1Editing(false);
                    }// Save new standard API
                }

            } catch (error) {
                openNotification("Error while saving standard", 'error');
            }
        },
    });

    const handleStep1Edit = (standard) => {
        setIsStep1Editing(true);
        setOpenEdit(true);
        formikStep1.setValues({
            id: standard.id,
            name: standard.name,
            description: standard.description,
            acronym: standard.acronym || '',
            group: standard.group,
            status: standard.status
        });

    };

    const handleSync = async (standard) => {
        const standardId = standard;
        try {
            const response = await syncMasterStandardsById(standardId);
            if (response.status === 200) {
                openNotification("Standard synced successfully", 'success');
            }
            else {
                openNotification("Error while syncing standard", 'error');
            }

        }
        catch (error) {
            openNotification("Error while syncing standard", 'error');
        }

    };



    useEffect(() => {
        fetchStandardsData();
        formikStep1.resetForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [standardType]);



    const handleStatusChange = (event) => {
        const newStatus = event.target.value === 'true';
        if (!newStatus) { // If status is being changed to 'Inactive'
            setOpen(true);

        } else {
            formikStep1.setFieldValue('status', true); // Set status to 'Active'
        }
    };

    const handleConfirmInactive = () => {
        formikStep1.setFieldValue('status', false); // Set status to 'Inactive'
        setOpen(false); // Close the dialog
    };

    const handleClose = () => {
        setOpen(false);
    };


    // Close dialog
    const handleCloseEdit = () => {
        setOpenEdit(false);
        formikStep1.resetForm(); // Reset the form when closing the dialog
    };

    // Handles to download a default template
    const downloadDefaultTemplate = () => {
        const data = [
            ["name", "description", "ControlID", "Title", "ControlDescription", "ControlWeightage", "SubControlID", "SubControlName", "Question_ID", "Question", "SubControlDes"],
            ["Ransomeware", "the actions and measures taken by organizations to prevent, detect, and respond to ransomware attacks in order to comply with relevant laws, regulations, and industry standards.", "1", "Governance", "Test control", "4", "1.1", "Ransomware Strategy/Policy", "1.01.01", "A vulnerability management policy that supports the enterprise's vulnerability management program establishes the requirement to proactively identify, prioritize and remediate vulnerabilities within the enterprise to align with its risk tolerance.", "Test Sub Control"],
            ["Ransomeware", "the actions and measures taken by organizations to prevent, detect, and respond to ransomware attacks in order to comply with relevant laws, regulations, and industry standards.", "1", "Governance", "Test control", "4", "1.1", "Ransomware Strategy/Policy", "1.01.02", "A threat intelligence management policy establishes the requirement to proactively collect, analyze, assess, report and advise on threats faced by the enterprise.", "Test Sub Control"],
            ["Ransomeware", "the actions and measures taken by organizations to prevent, detect, and respond to ransomware attacks in order to comply with relevant laws, regulations, and industry standards.", "1", "Governance", "", "4", "1.2", "	Risk Assessment", "1.02.01", "The enterprise has deployed application execution (permit/deny) technologies to computing resources (e.g., workstations, mobile devices, servers).", "Test Sub Control"],
            ["Ransomeware", "the actions and measures taken by organizations to prevent, detect, and respond to ransomware attacks in order to comply with relevant laws, regulations, and industry standards.", "2", "Asset Inventory and Management", "", "4", "2.1", "Asset Inventory and Management", "2.01.01", "The enterprise has network forensic capabilities to analyze network flow captured within the environment, and the enterprise retains network flow captures for a defined period of time.", "Test Sub Control"],
            ["Ransomeware", "the actions and measures taken by organizations to prevent, detect, and respond to ransomware attacks in order to comply with relevant laws, regulations, and industry standards.", "2", "Asset Inventory and Management", "Test control", "4", "2.2", "Operational Processes", "2.02.01", "All data flows (i.e., where data are processed, stored and transmitted) within the enterprise, including flows to third parties, are documented with flowcharts and updated periodically as changes in data occur.", "Test Sub Control"],
        ];
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        XLSX.writeFile(wb, "template.xlsx");

    }

    // Method handles to extract the data from the selected excel file
    const handleFileRead = (file) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const binaryStr = e.target.result;
                const workbook = XLSX.read(binaryStr, { type: 'binary' });

                const sheetName = workbook.SheetNames[0];

                const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]);
                const templateData = sheetData.map((data) => ({
                    ...data,
                    // company: companyId,
                    group: standardType === 'standard-based' ? 'Main Standard' : 'Domain Standard',
                    status: true,
                }))
                setFileData(templateData);
            } catch (error) {
                console.error('Error parsing file:', error);
            }
        };
        reader.readAsBinaryString(file);
    };

    // Handles to upload the file
    const handleFileUpload = async () => {
        let response = await createNewStandard(fileData);
        if (response?.status === 201) {
            openNotification(response?.data?.message, 'success');
        }
        else {
            openNotification(response?.data?.message, 'error');
        }
    }

    // This method handles to select an excel file
    const handleFileSelect = (info) => {
        const file = info.fileList[0]?.originFileObj;
        const newFileList = info?.fileList?.slice(0, 1);
        setFileList(newFileList);
        if (info?.fileList?.length > 1) {
            openNotification('Please select single excel file only');
            return;
        }
        if (file && file?.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
            setFileData([]);
            handleFileRead(file);

        }
        else if (file && file?.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
            openNotification('Please upload only excel type file', 'error');
        }
    };


    const tableContainerStyle = {
        marginBottom: '20px',
    };
    const dividerStyle = {
        margin: '20px 0',
    };
    const radioGroupStyle = {
        display: 'flex',
        flexDirection: 'row',
        gap: '10px',
    };
    return (
        <div className='main-cont'>
            <Card>
                <CardHeader
                    title={
                        <React.Fragment>
                            <div className="card-head">
                                <div className="flex-head">
                                    <label className="label">
                                        <Breadcrumbs paths={['CAP Configurator', 'Configure Master Standards']} />
                                    </label>
                                </div>
                                <div>
                                    <Button
                                        variant="contained"
                                        className="back font"
                                        onClick={() => window.history.back()}
                                    >
                                        Back
                                    </Button>
                                </div>
                            </div>
                        </React.Fragment>
                    }
                />
                <CardContent>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Select Standard Type</FormLabel>
                        <RadioGroup
                            aria-label="standard-type"
                            name="standardType"
                            value={standardType}
                            onChange={(e) => setStandardType(e.target.value)}
                            style={radioGroupStyle} // Apply the styles here
                        >
                            {standardTypes.map((type) => (
                                <FormControlLabel
                                    key={type.id}
                                    value={type.id}
                                    control={<Radio />}
                                    label={type.name}
                                />
                            ))}
                        </RadioGroup>
                        <b>Standard Based 70% & Domain Based 30%</b>
                    </FormControl>
                    {standardType && (
                        <>
                            <CardContent>

                                <CardContent>
                                    {/* Button to open the dialog */}
                                    <div style={{ textAlign: 'end' }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => { setDialogOpen(true) }}
                                        >
                                            Add Standard
                                        </Button>

                                    </div>

                                    {/* Dialog Box */}
                                    <Dialog
                                        open={dialogOpen}
                                        onClose={() => setDialogOpen(false)}
                                        fullWidth
                                        maxWidth="sm"
                                    >
                                        <DialogTitle className='fs-5 font-weight-bold'>
                                            Standard Creation Type
                                        </DialogTitle>
                                        <Divider />
                                        <DialogContent>
                                            <div className='d-flex justify-content-center align-items-center m-5'>
                                                <Button className='me-5 fs-6' variant="contained" onClick={() => {
                                                    navigate("/cap/adm-configurenewstandards");
                                                }}>Manual </Button>

                                                <Button variant="contained" className='fs-6' onClick={() => {
                                                    setFileData([]);
                                                    setFileList([]);
                                                    setFileUploadDialog(true);
                                                    setDialogOpen(false);
                                                }}>File Upload </Button>
                                            </div>
                                            <div className='d-flex justify-content-end'>
                                                <Button variant="contained" className="danger fs-6" onClick={() => setDialogOpen(false)}>
                                                    Close
                                                </Button>
                                            </div>

                                        </DialogContent>

                                    </Dialog>

                                    <Dialog
                                        open={fileUploadDialog}
                                        onClose={() => {
                                            setFileList([])
                                            setFileUploadDialog(false);
                                        }}
                                        fullWidth
                                        maxWidth="sm"
                                    >
                                        <DialogTitle>
                                            File Upload
                                        </DialogTitle>
                                        <Divider />
                                        <DialogContent>
                                            <div className='d-flex justify-content-center'>
                                                <Dragger
                                                    className="upload-demo m-4"
                                                    accept=".xlsx"
                                                    beforeUpload={() => false}
                                                    onChange={handleFileSelect}
                                                    fileList={fileList}
                                                >
                                                    <p className="ant-upload-drag-icon d-flex align-items-center  px-3" >
                                                        <CloudUploadOutlined style={{ marginRight: 8 }} />
                                                        <span className="ant-upload-text">Drop file here or click to upload</span>
                                                    </p>
                                                </Dragger>
                                                <span class="d-flex justify-content-center align-items-center text-primary text-decoration-underline" onClick={downloadDefaultTemplate}>download template</span>
                                            </div>
                                            <div className="note-content m-3">
                                                <p>
                                                    <strong>Note:</strong> Alteast <em>One Sub Control</em> or <code>One Question</code>,
                                                    is <code>mandatory</code>.
                                                </p>
                                            </div>
                                            <div className='d-flex justify-content-end'>
                                                <span>
                                                    <Button variant="contained" className="primary me-3" onClick={handleFileUpload}>
                                                        Upload
                                                    </Button>
                                                    <Button variant="contained" className="danger" onClick={() => { setFileUploadDialog(false) }}>
                                                        Close
                                                    </Button>
                                                </span>
                                            </div>
                                        </DialogContent>
                                    </Dialog>
                                    {/* Dialog Box */}
                                    <Dialog
                                        open={openEdit}
                                        onClose={handleCloseEdit} // Close the dialog
                                        aria-labelledby="form-dialog-title"
                                        fullWidth
                                        maxWidth="sm" // Control dialog size
                                    >
                                        <DialogTitle id="form-dialog-title">
                                            {isStep1Editing ? "Edit Standard" : "Add Standard"}
                                        </DialogTitle>
                                        <DialogContent>
                                            <form onSubmit={formikStep1.handleSubmit}>
                                                <FormControl fullWidth margin="normal">
                                                    <TextField
                                                        label="Name"
                                                        name="name"
                                                        // disabled={standardType === 'standard-based'}
                                                        onChange={formikStep1.handleChange}
                                                        value={formikStep1.values.name}
                                                        error={formikStep1.touched.name && Boolean(formikStep1.errors.name)}
                                                        helperText={formikStep1.touched.name && formikStep1.errors.name}
                                                    />
                                                </FormControl>

                                                <FormControl fullWidth margin="normal">
                                                    <TextField
                                                        label="Description"
                                                        name="description"
                                                        // disabled={standardType === 'standard-based'}
                                                        onChange={formikStep1.handleChange}
                                                        value={formikStep1.values.description}
                                                        error={formikStep1.touched.description && Boolean(formikStep1.errors.description)}
                                                        helperText={formikStep1.touched.description && formikStep1.errors.description}
                                                        multiline
                                                        rows={3}
                                                        maxRows={Infinity}
                                                    />
                                                </FormControl>

                                                {isStep1Editing && (
                                                    <div className="row col-md-12 px-2" style={{ alignItems: 'center' }}>
                                                        <FormControl component="fieldset" margin="normal">
                                                            <RadioGroup
                                                                row
                                                                aria-label="status"
                                                                name="status"
                                                                value={formikStep1.values.status ? 'true' : 'false'}
                                                                onChange={handleStatusChange}
                                                            >
                                                                <FormControlLabel value="true" control={<Radio />} label="Active" />
                                                                <FormControlLabel value="false" control={<Radio />} label="Inactive" />
                                                            </RadioGroup>
                                                        </FormControl>
                                                    </div>
                                                )}
                                            </form>
                                        </DialogContent>

                                        {/* Dialog Actions */}
                                        <DialogActions>
                                            <Button onClick={handleCloseEdit} color="primary">
                                                Cancel
                                            </Button>
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                onClick={formikStep1.handleSubmit} // Trigger form submit on button click
                                                disabled={!isStep1Editing && standardType === 'standard-based'}
                                            >
                                                {isStep1Editing ? 'Update' : 'Save'}
                                            </Button>
                                        </DialogActions>
                                    </Dialog>

                                    <Dialog
                                        open={open}
                                        onClose={handleClose}
                                        aria-labelledby="alert-dialog-title"
                                        aria-describedby="alert-dialog-description"
                                    >
                                        <DialogTitle id="alert-dialog-title">{"Confirm Inactive Status"}</DialogTitle>
                                        <DialogContent>
                                            <DialogContentText id="alert-dialog-description">
                                                Would you really like to change the status to "Inactive"?.
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={handleClose} color="primary">
                                                Cancel
                                            </Button>
                                            <Button onClick={handleConfirmInactive} color="secondary" autoFocus>
                                                Yes
                                            </Button>
                                        </DialogActions>
                                    </Dialog>
                                </CardContent>

                                {selectedStandards && selectedStandards.length > 0 && (
                                    <div style={tableContainerStyle}>
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Description</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell>Actions</TableCell> {/* Add Actions column */}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {selectedStandards && selectedStandards.length > 0 && selectedStandards.map((standard, index) => (
                                                        <TableRow key={index}>
                                                            <TableCell>{standard.name}</TableCell>
                                                            <TableCell style={{ maxWidth: '300px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                                {standard.description}
                                                            </TableCell>
                                                            <TableCell>{standard.status ? 'Active' : 'Inactive'}</TableCell>
                                                            <TableCell>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    onClick={() => handleStep1Edit(standard)}
                                                                    style={{ marginRight: '5px' }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    onClick={() => handleSync(standard.id)}
                                                                >
                                                                    Sync
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        <TablePagination
                                            rowsPerPageOptions={[10, 25, 100]}
                                            component="div"
                                            count={selectedStandards.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                        />
                                    </div>
                                )}
                                <Divider style={dividerStyle} />
                            </CardContent>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
export default ConfigureStandards;
